import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { Autocomplete, Checkbox, TextField } from '@mui/material'
import { SyntheticEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLeftPadding } from '../../../store/redux/hooks'
import { User } from '../../../store/types'
import Cost from '../../../store/types/models/Cost'
import { StyledListItem } from '../../../styles/ProfileViewStyles'
import CollapsibleSection from '../../Button/CollapsibleSection'
import { Option } from '../../Form/Inputs/SelectInput'
import { costTypeOptions } from '../../Sidebar/cost/CostViewForm'
import FilterMenu from '../FilterMenu'

export type ProjectCostsFilterType = {
  employees: User[]
  costTypes: string[]
}

export const initialProjectCostsFilters: ProjectCostsFilterType = {
  employees: [],
  costTypes: [],
}

type FilterProjectCostsMenuProps = {
  setFilteringOptions: React.Dispatch<React.SetStateAction<ProjectCostsFilterType>>
  costs: Cost[]
}

const FilterProjectCostsMenu = ({ setFilteringOptions, costs }: FilterProjectCostsMenuProps) => {
  const leftPadding = useLeftPadding()
  const { t } = useTranslation()
  const [selectedFilters, setSelectedFilters] = useState<ProjectCostsFilterType>(initialProjectCostsFilters)
  const [selectedEmployees, setSelectedEmployees] = useState<User[]>([])
  const [selectedCostTypes, setSelectedCostTypes] = useState<Option[]>([])

  const employees = useMemo(() => {
    const uniqueEmployees = new Map<string, User>()
    costs.forEach(cost => {
      if (cost.author && !uniqueEmployees.has(cost.author.id)) {
        uniqueEmployees.set(cost.author.id, {
          id: cost.author.id,
          firstName: cost.author.firstName,
          lastName: cost.author.lastName,
        } as User)
      }
    })
    return Array.from(uniqueEmployees.values())
  }, [costs])

  const handleEmployeesChange = (_event: SyntheticEvent<Element, Event>, value: User[]) => {
    setSelectedEmployees(value)
    setSelectedFilters(prev => ({
      ...prev,
      employees: value,
    }))
  }

  const handleCostTypesChange = (_event: SyntheticEvent<Element, Event>, value: Option[]) => {
    setSelectedCostTypes(value)
    setSelectedFilters(prev => ({
      ...prev,
      costTypes: value.map(v => v.value),
    }))
  }

  const handleApply = () => {
    setFilteringOptions(selectedFilters)
  }

  const handleClear = () => {
    setSelectedEmployees([])
    setSelectedCostTypes([])
    setSelectedFilters(initialProjectCostsFilters)
    setFilteringOptions(initialProjectCostsFilters)
  }

  const icon = <CheckBoxOutlineBlank fontSize="small" />
  const checkedIcon = <CheckBox fontSize="small" />

  return (
    <FilterMenu filter={handleApply} clear={handleClear}>
      <CollapsibleSection text={t('page.costSummary.table.header.payer')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          multiple
          disableCloseOnSelect
          value={selectedEmployees}
          options={employees}
          onChange={handleEmployeesChange}
          getOptionLabel={option => `${option.firstName} ${option.lastName}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option, { selected }) => {
            const { key, ...other } = props
            return (
              <StyledListItem key={key} {...other}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8, marginLeft: -8 }}
                  checked={selected}
                />
                {option.firstName} {option.lastName}
              </StyledListItem>
            )
          }}
          renderInput={params => <TextField {...params} size="small" label={t('page.costSummary.selectPayer')} />}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.costSummary.table.header.type')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          multiple
          disableCloseOnSelect
          limitTags={3}
          value={selectedCostTypes}
          onChange={handleCostTypesChange}
          options={costTypeOptions}
          getOptionLabel={option => t(option.label)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={(props, option, { selected }) => {
            const { key, ...other } = props
            return (
              <StyledListItem key={key} {...other}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8, marginLeft: -8 }}
                  checked={selected}
                />
                {t(option.label)}
              </StyledListItem>
            )
          }}
          renderInput={params => <TextField {...params} size="small" label={t('page.costSummary.selectType')} />}
        />
      </CollapsibleSection>
    </FilterMenu>
  )
}

export default FilterProjectCostsMenu
